<script setup lang="ts">
import { ref } from 'vue';
import TextCheckOptions from './TextCheckOptions.vue';

const emit = defineEmits<{
  (e: 'changeScoreThreshold', value: number): void;
}>();

const props = defineProps<{
  initialScoreThreshold: number;
}>();
const scoreThreshold = ref(props.initialScoreThreshold);

const scoreOptions = [
  {
    value: 3,
    badge: '3+',
    title: 'Cast a wider net',
    description: 'Keep your pipeline full with promising candidates who meet basic requirements',
  },
  {
    value: 4,
    badge: '4+',
    title: 'Focus on strong matches',
    description: 'Prioritize candidates who demonstrate solid qualifications and clear potential',
  },
  {
    value: 4.5,
    badge: '4.5+',
    title: 'Only exceptional candidates',
    description: 'Limit to top-tier candidates with the highest likelihood of success',
  },
];

function handleScoreThresholdChange(value: number) {
  scoreThreshold.value = value;
  emit('changeScoreThreshold', value);
}
</script>

<template>
  <div>
    <h3 class="mb-4 text-base font-bold text-black">
      Which candidates should your AI recruiter book interviews with?
    </h3>

    <div class="flex flex-col gap-3">
      <TextCheckOptions
        v-for="option in scoreOptions"
        :key="option.value"
        :is-selected="scoreThreshold === option.value"
        @click="handleScoreThresholdChange(option.value)"
      >
        <!-- Non selected state -->
        <template #title>
          <div class="flex items-center gap-4 text-shade-900">
            <div
              class="flex min-w-20 items-center justify-center gap-1 rounded-md p-2 font-extrabold text-tint-400"
              :class="{
                'bg-[#F7EAFF] text-highlight-500': scoreThreshold === option.value,
                'bg-tint-40 ': scoreThreshold !== option.value,
              }"
            >
              <img
                v-if="scoreThreshold !== option.value"
                src="@/assets/svg/star-outline.svg"
                class="h-6 w-6"
              />
              <img v-else src="@/assets/svg/star-filled-highlight.svg" class="h-6 w-6" alt="star" />

              <span class="ml-1 text-base font-extrabold">
                {{ option.badge }}
              </span>
            </div>

            <div class="flex flex-col gap-1">
              <p class="text-base font-bold">{{ option.title }}</p>
              <p class="text-sm font-normal text-shade-820">{{ option.description }}</p>
            </div>
          </div>
        </template>
      </TextCheckOptions>
    </div>
  </div>
</template>
